import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { SeriesBadge } from "@/components/SeriesBadge";

type LinkRow = { label: string; to: string };
type SeriesEntry = {
  slug: string;
  name: string;
  eyebrow: string;
  tagline: string;
  intro: string;
  overview: string;
  audience: string;
  cities: string[];
  solutions: LinkRow[];
  services: LinkRow[];
  primaryCta: { label: string; to: string };
  flagshipPath?: string;
  metaTitle: string;
  metaDescription: string;
  keywords: string;
};

const SERIES: Record<string, SeriesEntry> = {
  "alamo-series": {
    slug: "alamo-series",
    name: "Alamo Series",
    eyebrow: "Residential Shade & Awnings",
    tagline: "Built-in-Texas shade for Texas homes.",
    intro:
      "The Alamo Series is our residential division — custom awnings, patio covers, sail shades, and retractable systems built for homes across San Antonio, Austin, and the Hill Country.",
    overview:
      "Every Alamo Series build is fabricated in our Texas shop with Sunbrella® canvas, welded aluminum frames, and hardware chosen to survive Texas sun, storms, and wind. From a small back-door awning to a full patio recover, this series covers the residential lineup end-to-end.",
    audience: "Homeowners across South and Central Texas.",
    cities: ["San Antonio", "Austin", "New Braunfels", "Boerne", "Seguin"],
    solutions: [
      { label: "Custom Awnings", to: "/custom-awnings" },
      { label: "Residential Awnings", to: "/residential-awnings" },
      { label: "Retractable Awnings", to: "/retractable-awnings" },
      { label: "Patio Covers", to: "/patio-covers" },
      { label: "Pergola Covers", to: "/pergola-covers" },
      { label: "Sail Shades", to: "/sail-shades" },
      { label: "Exterior Vertical Shades", to: "/exterior-vertical-shades" },
      { label: "Outdoor Curtains", to: "/curtains" },
      { label: "Pool & Spa Shade", to: "/pool-spa" },
    ],
    services: [
      { label: "Recover an Existing Awning", to: "/recover-awnings" },
      { label: "See Our Fabrics", to: "/fabrics" },
      { label: "View Projects", to: "/projects" },
    ],
    primaryCta: { label: "Get a Residential Quote", to: "/estimate-quotes#general" },
    flagshipPath: "/alamo-series",
    metaTitle: "Alamo Series — Residential Awnings & Shade | Texas Made Shade",
    metaDescription:
      "Alamo Series — residential awnings, patio covers, sail shades and retractables custom-built in Texas for San Antonio, Austin and the Hill Country.",
    keywords:
      "alamo series, residential awnings texas, custom awnings san antonio, patio covers austin, sail shades hill country, retractable awnings texas",
  },
  "hill-country-series": {
    slug: "hill-country-series",
    name: "Hill Country Series",
    eyebrow: "Outdoor Living & Premium Shade",
    tagline: "Luxury outdoor living for Hill Country estates.",
    intro:
      "Hill Country Series is our luxury retractable line — motorized awnings, screen systems, and architect-specified structures for estates from Boerne to Lake Austin.",
    overview:
      "Built around Solair® retractable systems, motorized exterior screens, and custom pergolas, the Hill Country Series is designed for high-end homes that want protection from the sun without giving up the view. Wind sensors, remotes, and smart-home integration are standard options.",
    audience: "Luxury Hill Country and Lake Austin homeowners, builders, and interior designers.",
    cities: ["Boerne", "Fredericksburg", "Kerrville", "Wimberley", "Austin"],
    solutions: [
      { label: "Solair® Retractable Awnings", to: "/retractable-awnings" },
      { label: "Solair® vs. Competitors", to: "/solair-vs" },
      { label: "Exterior Vertical Shades", to: "/exterior-vertical-shades" },
      { label: "Retractable Awnings", to: "/retractable-awnings" },
      { label: "Pergola Covers", to: "/pergola-covers" },
      { label: "Custom Awnings", to: "/custom-awnings" },
    ],
    services: [
      { label: "Architect & Designer Program", to: "/architects" },
      { label: "Hill Country Projects", to: "/hill-country" },
      { label: "View Our Work", to: "/view-our-work" },
    ],
    primaryCta: { label: "Request a Luxury Consult", to: "/estimate-quotes#general" },
    flagshipPath: "/hill-country-series",
    metaTitle: "Hill Country Series — Outdoor Living & Premium Shade | Texas Made Shade",
    metaDescription:
      "Hill Country Series — luxury retractable awnings, Solair® systems and motorized screens for Boerne, Fredericksburg, Kerrville and Lake Austin estates.",
    keywords:
      "hill country series, luxury retractable awnings, solair texas, motorized screens boerne, fredericksburg awnings, lake austin shade",
  },
  "longhorn-series": {
    slug: "longhorn-series",
    name: "Longhorn Series",
    eyebrow: "Commercial Shade Systems",
    tagline: "Storefront to schoolyard — commercial-grade shade.",
    intro:
      "Longhorn Series is our commercial division — storefront awnings, restaurant patios, backlit signage, covered parking, and welded aluminum shade structures across Texas.",
    overview:
      "Commercial jobs need drawings, engineering letters, code-compliant fabric, and hardware that lasts a lease. Longhorn Series bundles the fabrication, install, and paperwork commercial GCs, property managers, and franchise owners expect.",
    audience: "Restaurants, retail, offices, franchise operators, and property managers.",
    cities: ["San Antonio", "Austin", "Houston", "Corpus Christi", "Statewide"],
    solutions: [
      { label: "Commercial Awnings", to: "/commercial-awnings" },
      { label: "Restaurant Awnings", to: "/restaurant-awnings" },
      { label: "Backlit / Illuminated Awnings", to: "/backlit-awnings" },
      { label: "Metal Awnings", to: "/metal-awnings" },
      { label: "Covered Parking", to: "/covered-parking" },
      { label: "Car Wash Shade Sails", to: "/car-wash-shade-sails" },
      { label: "Sail Shades", to: "/sail-shades" },
    ],
    services: [
      { label: "Custom Fabrication & Welding", to: "/custom-fabrication" },
      { label: "Commercial Case Studies", to: "/case-studies" },
      { label: "Request a Commercial Bid", to: "/estimate-quotes" },
    ],
    primaryCta: { label: "Request a Commercial Bid", to: "/estimate-quotes#general" },
    flagshipPath: "/longhorn-series",
    metaTitle: "Longhorn Series — Commercial Awnings & Shade | Texas Made Shade",
    metaDescription:
      "Longhorn Series — commercial awnings, restaurant patios, backlit signage, covered parking and welded shade structures built in Texas.",
    keywords:
      "longhorn series, commercial awnings texas, restaurant awnings, backlit awnings, covered parking, commercial shade structures san antonio austin houston",
  },
  "rio-grande-series": {
    slug: "rio-grande-series",
    name: "Rio Grande Series",
    eyebrow: "Hospitality & Resorts",
    tagline: "Hotels • Resorts • Glamping • Safari Tents • Spas • Retreats • Event Venues",
    intro:
      "Rio Grande Series is our hospitality lineup — resort cabanas, glamping and safari tents, spa and wellness shade, hotel patio awnings, restaurant drop curtains and event-venue structures built for guest-facing spaces.",
    overview:
      "Hospitality shade has to look great in a brochure and survive a full season of guests, wind, and salt air. Rio Grande projects use fade-resistant Sunbrella® canvas, corrosion-rated hardware, and finishes matched to brand standards.",
    audience: "Hotels, resorts, glamping operators, spas, ranch retreats, event venues, boutique lodging and restaurants.",
    cities: ["San Antonio", "Austin", "Corpus Christi", "South Padre Island", "Port Aransas"],
    solutions: [
      { label: "Restaurant Awnings", to: "/restaurant-awnings" },
      { label: "Commercial Awnings", to: "/commercial-awnings" },
      { label: "Sail Shades", to: "/sail-shades" },
      { label: "Pool & Spa Shade", to: "/pool-spa" },
      { label: "Outdoor Curtains", to: "/curtains" },
      { label: "Exterior Vertical Shades", to: "/exterior-vertical-shades" },
    ],
    services: [
      { label: "Custom Fabrication", to: "/custom-fabrication" },
      { label: "Case Studies", to: "/case-studies" },
      { label: "Coastal Projects", to: "/gulf-coast" },
    ],
    primaryCta: { label: "Request a Hospitality Quote", to: "/estimate-quotes#general" },
    flagshipPath: "/rio-grande-series",
    metaTitle: "Rio Grande Series — Hospitality & Resorts, Glamping & Safari Tents | Texas Made Shade",
    metaDescription:
      "Rio Grande Series — hospitality & resort shade: resort cabanas, glamping and safari tents, spa shade, hotel patio awnings, restaurant drop curtains and event shade across Texas.",
    keywords:
      "rio grande series, hospitality shade, resort cabanas, glamping tents texas, safari tents, spa and wellness shade, ranch retreat lodging, hotel awnings texas, restaurant patio shade, event venue shade south padre corpus christi",
  },
  "republic-series": {
    slug: "republic-series",
    name: "Republic Series",
    eyebrow: "Government & Schools",
    tagline: "Bid-ready shade for schools, cities and public agencies.",
    intro:
      "Republic Series is our government, school, and public-works division — playground shade, athletic dugouts, bus canopies and municipal awnings built to specification.",
    overview:
      "We work with ISD facilities teams, city procurement, and A/E firms to deliver bid-compliant shade with the drawings, submittals and warranty letters your project needs. Fabric options include commercial-grade Sunbrella® and HDPE shade cloth rated for playground use.",
    audience: "School districts, cities, counties, and government buyers.",
    cities: ["San Antonio ISD", "Austin ISD", "New Braunfels ISD", "Seguin ISD", "Statewide"],
    solutions: [
      { label: "Playground Shade Structures", to: "/playground-shade" },
      { label: "School Shade Structures", to: "/school-shade-structures" },
      { label: "Schools & Athletics", to: "/schools-athletics" },
      { label: "Church Shade Structures", to: "/church-shade-structures" },
      { label: "Covered Parking & Walkways", to: "/covered-parking" },
      { label: "Sail Shades", to: "/sail-shades" },
    ],
    services: [
      { label: "Custom Fabrication & Welding", to: "/custom-fabrication" },
      { label: "Case Studies", to: "/case-studies" },
      { label: "Request a Public-Works Bid", to: "/estimate-quotes" },
    ],
    primaryCta: { label: "Request a Public-Works Bid", to: "/estimate-quotes#general" },
    flagshipPath: "/republic-series",
    metaTitle: "Republic Series — Government & School Shade Structures | Texas Made Shade",
    metaDescription:
      "Republic Series — bid-ready playground shade, athletic covers, school windscreens and municipal awnings for Texas ISDs and government agencies.",
    keywords:
      "republic series, playground shade texas, school shade structures, dugout covers, isd shade, government awnings, municipal shade texas",
  },
  "revival-series": {
    slug: "revival-series",
    name: "Revival Series",
    eyebrow: "Service & Recovery",
    tagline: "We recover, repair and re-canvas — even if we didn't build it.",
    intro:
      "Revival Series is our service division — we recover, repair, and re-canvas awnings, boat covers, retractable systems and canopies from any manufacturer.",
    overview:
      "Bring us a torn awning, a boat cover with a stuck zipper, or a retractable that stopped retracting. Revival covers frame repair, motor service, re-canvas jobs, and zipper/thread replacement so a tired system gets another decade of life.",
    audience: "Anyone with a tired awning, torn cover, or aging retractable — no matter who built it.",
    cities: ["San Antonio", "Austin", "New Braunfels", "Seguin", "Corpus Christi"],
    solutions: [
      { label: "Recover Awnings", to: "/recover-awnings" },
      { label: "Boat Cover Repair & Recover", to: "/boat-covers" },
      { label: "RV Cover Repair", to: "/rv-covers" },
      { label: "Retractable Awning Service", to: "/retractable-awnings" },
      { label: "Trailer Cover Recover", to: "/trailer-covers" },
      { label: "Stock Trailer Covers", to: "/stock-trailer-covers" },
    ],
    services: [
      { label: "See Our Fabrics", to: "/fabrics" },
      { label: "Ranger Care Membership", to: "/ranger-care" },
      { label: "Request a Service Quote", to: "/estimate-quotes" },
    ],
    primaryCta: { label: "Request a Service Quote", to: "/estimate-quotes#general" },
    flagshipPath: "/revival-series",
    metaTitle: "Revival Series — Awning & Cover Repair, Recover & Service | Texas Made Shade",
    metaDescription:
      "Revival Series — awning recover, boat cover repair, retractable motor service and re-canvas work across Texas. We service what we build and what we didn't.",
    keywords:
      "revival series, awning recover texas, boat cover repair, retractable awning service, re-canvas awning, san antonio awning repair, austin awning service",
  },
  "lone-star-elite": {
    slug: "lone-star-elite",
    name: "Lone Star Series",
    eyebrow: "Smart Lighting & Automation",
    tagline: "One-of-a-kind builds. Architect, designer and dream projects.",
    intro:
      "Lone Star Series is our custom flagship — bespoke welded shade sculptures, motorized smart-home retractables, and architect-collaboration projects with no template.",
    overview:
      "Elite jobs start on a whiteboard: sculptural steel canopies, integrated lighting, wind-sensor-driven retractables, and finishes matched to the architecture. We work directly with architects, designers, and homeowners who want something that has never been built before.",
    audience: "Architects, designers, and clients with custom-build ambitions.",
    cities: ["San Antonio", "Austin", "Hill Country", "Statewide"],
    solutions: [
      { label: "Solair® Retractable Awnings", to: "/retractable-awnings" },
      { label: "Custom Fabrication & Welding", to: "/custom-fabrication" },
      { label: "Custom Awnings", to: "/custom-awnings" },
      { label: "Backlit / Illuminated Awnings", to: "/backlit-awnings" },
      { label: "Exterior Vertical Shades", to: "/exterior-vertical-shades" },
      { label: "Sail Shades", to: "/sail-shades" },
    ],
    services: [
      { label: "Architect & Designer Program", to: "/architects" },
      { label: "Featured Projects", to: "/projects" },
      { label: "Case Studies", to: "/case-studies" },
    ],
    primaryCta: { label: "Start a Custom Project", to: "/estimate-quotes#general" },
    flagshipPath: "/lone-star-elite",
    metaTitle: "Lone Star Series — Custom Flagship & Smart Shade | Texas Made Shade",
    metaDescription:
      "Lone Star Series — bespoke welded shade sculptures, motorized retractables and architect-collaboration projects across Texas.",
    keywords:
      "lone star elite, custom shade texas, architect awnings, bespoke pergola, smart retractable awnings, sculptural shade san antonio austin",
  },
  "trail-boss": {
    slug: "trail-boss",
    name: "Trail Boss Series",
    eyebrow: "Welding & Custom Fabrication",
    tagline: "Aluminum and steel fabrication for trailers, ranch and industry.",
    intro:
      "Trail Boss is our welding and metal-fabrication division — aluminum and steel work for trailers, ranch equipment, custom frames and repair jobs across Texas.",
    overview:
      "From a broken trailer gate to a fully welded stock-trailer top, Trail Boss handles the metal side of the business. Frames for our own awning and cover work start here, and we take on outside welding and fabrication for ag, trailer, and industrial customers.",
    audience: "Trailer owners, ranchers, commercial contractors, and industrial buyers.",
    cities: ["San Antonio", "Austin", "Seguin", "New Braunfels", "Statewide"],
    solutions: [
      { label: "Custom Fabrication & Welding", to: "/custom-fabrication" },
      { label: "Trailer Covers", to: "/trailer-covers" },
      { label: "Stock Trailer Covers", to: "/stock-trailer-covers" },
      { label: "Trailer Wiring", to: "/trailer-wiring" },
      { label: "Deer Blinds & Hunting", to: "/deer-blinds-and-hunting" },
      { label: "Hog Traps & Cages", to: "/hog-traps-and-cages" },
      { label: "Dog Boxes", to: "/dog-boxes" },
      { label: "Metal Awnings", to: "/metal-awnings" },
    ],
    services: [
      { label: "Aluminum Patio Covers (Austin)", to: "/aluminum-patio-covers/in/austin" },
      { label: "Aluminum Patio Covers (San Antonio)", to: "/aluminum-patio-covers/in/san-antonio" },
      { label: "Request a Fab Quote", to: "/estimate-quotes" },
    ],
    primaryCta: { label: "Request a Fab Quote", to: "/estimate-quotes#general" },
    flagshipPath: "/custom-fabrication",
    metaTitle: "Trail Boss Series — Aluminum & Steel Welding | Texas Made Shade",
    metaDescription:
      "Trail Boss Series — aluminum and steel welding and custom fabrication for trailers, ranch equipment and industrial jobs across Texas.",
    keywords:
      "trail boss series, aluminum welding texas, steel fabrication, trailer welding, stock trailer covers, ranch fabrication, san antonio austin welding",
  },
  "ranger-care": {
    slug: "ranger-care",
    name: "Ranger Care",
    eyebrow: "Maintenance Memberships",
    tagline: "Annual care for everything we build.",
    intro:
      "Ranger Care is our annual maintenance membership — inspection, cleaning, motor checks, and priority repair for awnings, retractables, sail shades and covers.",
    overview:
      "One yearly visit keeps motors, fabric and hardware in working order. Members get priority scheduling when weather or wear happens, discounts on parts and labor, and extended warranty coverage on qualifying builds.",
    audience: "Existing Texas Made Shade customers and anyone with a shade investment worth protecting.",
    cities: ["San Antonio", "Austin", "New Braunfels", "Seguin", "Statewide"],
    solutions: [
      { label: "Retractable Awning Service", to: "/retractable-awnings" },
      { label: "Recover Awnings", to: "/recover-awnings" },
      { label: "Boat Cover Service", to: "/boat-covers" },
      { label: "RV Cover Service", to: "/rv-covers" },
      { label: "Sail Shade Care", to: "/sail-shades" },
    ],
    services: [
      { label: "Revival Series — Repairs", to: "/revival-series" },
      { label: "Ranger Care Membership Details", to: "/ranger-care" },
      { label: "Contact Service Team", to: "/contact" },
    ],
    primaryCta: { label: "Join Ranger Care", to: "/estimate-quotes#general" },
    flagshipPath: "/ranger-care",
    metaTitle: "Ranger Care — Annual Shade & Awning Maintenance | Texas Made Shade",
    metaDescription:
      "Ranger Care — annual membership for inspection, cleaning, priority repair and warranty coverage on awnings, retractables and covers across Texas.",
    keywords:
      "ranger care, awning maintenance texas, retractable service plan, shade maintenance membership, annual awning inspection",
  },
};

export const Route = createFileRoute("/series/$slug")({
  loader: ({ params }) => {
    // Bluebonnet Series is a single flagship page (tents & outdoor markets).
    if (params.slug === "bluebonnet-series") {
      throw redirect({ to: "/bluebonnet-series" });
    }
    const entry = SERIES[params.slug];
    if (!entry) throw notFound();
    return { entry };
  },
  head: (ctx) => {
    const entry = ctx.loaderData?.entry;
    if (!entry) {
      return {
        meta: routeMeta(ctx, {
          title: "Series — Texas Made Shade",
          description: "Explore every Texas Made Shade signature series.",
        }),
      };
    }
    return {
      // Series alias pages restate their flagship page — point the canonical at
      // the flagship so crawlers index one URL per series, not two.
      links: [canonicalLink(entry.flagshipPath ?? pathFromMatch(ctx))],
      meta: routeMeta(ctx, {
        title: entry.metaTitle,
        description: entry.metaDescription,
        keywords: entry.keywords,
        socialTitle: entry.metaTitle,
        socialDescription: entry.metaDescription,
      }),
    };
  },
  component: SeriesLanding,
  notFoundComponent: SeriesNotFound,
});

function SeriesNotFound() {
  return (
    <div className="min-h-screen bg-cream">
      <SiteHeader />
      <main id="main-content" tabIndex={-1} className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-black uppercase text-navy-deep">Series Not Found</h1>
        <p className="mt-4 text-navy-deep/80">That series doesn't exist yet.</p>
        <Link to="/series" className="mt-8 inline-block rounded bg-red px-6 py-3 font-bold text-white">
          See All Series
        </Link>
      </main>
      <SiteFooter />
    </div>
  );
}

function SeriesLanding() {
  const { entry } = Route.useLoaderData() as { entry: SeriesEntry };
  return (
    <div className="min-h-screen bg-cream">
      <SiteHeader />
      <Breadcrumbs items={[{ label: "Series", to: "/series" }, { label: entry.name }]} />

      <main id="main-content" tabIndex={-1} className="mx-auto max-w-6xl px-4 py-12 lg:px-8 lg:py-16">
        <header className="mb-12 flex flex-col items-center text-center">
          <SeriesBadge slug={entry.slug} size="lg" priority />
          <p className="mt-6 text-sm font-bold uppercase tracking-widest text-red">{entry.eyebrow}</p>
          <h1 className="mt-2 font-display text-4xl font-black uppercase tracking-tight text-navy-deep md:text-5xl">
            {entry.name}
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-navy-deep/80">{entry.tagline}</p>
        </header>

        <section className="mx-auto mb-14 max-w-3xl text-center">
          <p className="text-lg text-navy-deep/85">{entry.intro}</p>
          <p className="mt-4 text-base text-navy-deep/75">{entry.overview}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to={entry.primaryCta.to as string}
              className="rounded bg-red px-6 py-3 font-bold uppercase tracking-wide text-white transition hover:bg-red/90"
            >
              {entry.primaryCta.label}
            </Link>
            {entry.flagshipPath && entry.flagshipPath !== `/series/${entry.slug}` && (
              <Link
                to={entry.flagshipPath as string}
                className="rounded border-2 border-navy-deep px-6 py-3 font-bold uppercase tracking-wide text-navy-deep transition hover:bg-navy-deep hover:text-white"
              >
                See Full {entry.name} Page
              </Link>
            )}
          </div>
        </section>

        <section className="mb-14">
          <h2 className="mb-6 font-display text-2xl font-bold uppercase tracking-wide text-navy-deep">
            Products in this Series
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {entry.solutions.map((s) => (
              <Link
                key={s.to}
                to={s.to as string}
                className="group flex items-center justify-between rounded-lg border border-navy-deep/10 bg-white px-5 py-4 shadow-sm transition hover:-translate-y-0.5 hover:border-red hover:shadow-md"
              >
                <span className="font-semibold text-navy-deep group-hover:text-red">{s.label}</span>
                <span aria-hidden className="text-red">→</span>
              </Link>
            ))}
          </div>
        </section>

        <section className="mb-14">
          <h2 className="mb-6 font-display text-2xl font-bold uppercase tracking-wide text-navy-deep">
            Related Services & Resources
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {entry.services.map((s) => (
              <Link
                key={s.to}
                to={s.to as string}
                className="rounded-lg border border-navy-deep/10 bg-navy-deep/5 px-5 py-4 font-semibold text-navy-deep transition hover:bg-navy-deep hover:text-white"
              >
                {s.label}
              </Link>
            ))}
          </div>
        </section>

        <section className="mb-14 rounded-lg bg-white p-8 shadow-sm">
          <h2 className="mb-3 font-display text-xl font-bold uppercase tracking-wide text-navy-deep">Who it's for</h2>
          <p className="text-navy-deep/85">{entry.audience}</p>
          <div className="mt-6">
            <div className="mb-2 text-sm font-bold uppercase tracking-widest text-red">Serving</div>
            <div className="flex flex-wrap gap-2">
              {entry.cities.map((c) => (
                <span
                  key={c}
                  className="rounded-full border border-navy-deep/15 bg-cream px-3 py-1 text-sm text-navy-deep/80"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className="rounded-lg bg-navy-deep p-10 text-center text-white">
          <h2 className="font-display text-2xl font-black uppercase tracking-wide">Ready to build?</h2>
          <p className="mx-auto mt-3 max-w-xl text-white/85">
            Tell us about your space. We'll send a {entry.name} quote tailored to your project — usually the same
            business day.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to={entry.primaryCta.to as string}
              className="rounded bg-red px-6 py-3 font-bold uppercase tracking-wide text-white transition hover:bg-red/90"
            >
              {entry.primaryCta.label}
            </Link>
            <Link
              to="/series"
              className="rounded border-2 border-white px-6 py-3 font-bold uppercase tracking-wide text-white transition hover:bg-white hover:text-navy-deep"
            >
              See All Series
            </Link>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
